import React from "react";
import {
  Box,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import {
  RateLimitCounter,
  dimensionShortLabel,
  formatCountdown,
  tierMeta,
} from "./rateLimit";

interface PropsInterface {
  counters: Array<RateLimitCounter>;
}

export const OtpCounterTable: React.FC<PropsInterface> = ({ counters }) => {
  if (!counters.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No counters are being held against this identifier.
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tier</TableCell>
            <TableCell>Applies to</TableCell>
            <TableCell>Window</TableCell>
            <TableCell sx={{ minWidth: 190 }}>Used</TableCell>
            <TableCell align="right">Left</TableCell>
            <TableCell align="right">Resets in</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/*
           * Server order, untouched. The API sorts blocking first and then by
           * proximity to the limit, which makes row one the answer to "why is
           * this person stuck". Sorting here — even by something sensible like
           * tier name — buries it.
           */}
          {counters.map((counter, index) => {
            const idle = counter.used === 0;
            const meta = tierMeta(counter.tier);
            const percent =
              counter.limit > 0
                ? Math.min(100, (counter.used / counter.limit) * 100)
                : 0;

            return (
              <TableRow
                key={`${counter.tier}:${counter.dimension}:${counter.window}:${counter.value}:${index}`}
                sx={{
                  opacity: idle ? 0.5 : 1,
                  backgroundColor: counter.blocking
                    ? (theme) => alpha(theme.palette.error.main, 0.08)
                    : "transparent",
                }}
              >
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    rowGap={0.5}
                  >
                    <Tooltip title={meta.description || meta.label}>
                      {/* The raw tier, not the friendly label: this is the
                          string that appears in the server logs. */}
                      <Typography
                        variant="body2"
                        fontWeight={counter.blocking ? 700 : 400}
                        sx={{ fontFamily: "monospace" }}
                      >
                        {counter.tier}
                      </Typography>
                    </Tooltip>

                    {counter.blocking && (
                      <Chip size="small" color="error" label="Blocking" />
                    )}
                  </Stack>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {dimensionShortLabel(counter.dimension)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "block",
                      maxWidth: 240,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {counter.value}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">{counter.window}</Typography>
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ flexGrow: 1, minWidth: 90 }}>
                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        color={counter.blocking ? "error" : "primary"}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={counter.blocking ? 700 : 400}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {counter.used}/{counter.limit}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="body2">{counter.remaining}</Typography>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {idle ? "—" : formatCountdown(counter.resetsInSeconds)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
